/* ============================================================
   Kane Warranty Form — Client-side controller
   - Proof of purchase always required
   - Phone format helper
   - Three named photo slots (photo_1, photo_2, photo_3)
   - HEIC → JPEG conversion (iPhone photos)
   - Client-side image compression (fixes 4.5MB Vercel payload limit)
   - Multipart submission with loading state + success/error UX
   ============================================================ */

(function () {
  'use strict';

  const root = document.querySelector('[data-warranty-form]');
  if (!root) return;

  const form = root.querySelector('#WarrantyForm');
  const endpoint = root.dataset.endpoint;
  const submitBtn = form.querySelector('[data-submit]');
  const submitLabel = form.querySelector('[data-submit-label]');
  const spinner = form.querySelector('[data-spinner]');
  const formError = form.querySelector('[data-form-error]');
  const successBox = root.querySelector('[data-success]');

  const phoneInput = form.querySelector('[name="phone"]');

  // ---------- Config ----------
  const MAX_IMAGE_DIMENSION = 1600;   // longest edge, in px
  const JPEG_QUALITY = 0.85;
  const HEIC2ANY_URL = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';

  // Cache of processed files, keyed by input name. We swap the FileList with
  // our processed version at submit time.
  const processedFiles = {};

  // ---------- Phone format helper ----------
  phoneInput.addEventListener('input', function (e) {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    let formatted = digits;
    if (digits.length > 6) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length > 3) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length > 0) {
      formatted = `(${digits}`;
    }
    e.target.value = formatted;
  });

  // ---------- Attach processors to every file input ----------
  form.querySelectorAll('input[type="file"]').forEach(input => {
    input.addEventListener('change', async function () {
      const fieldName = input.name;
      delete processedFiles[fieldName];

      if (!input.files || input.files.length === 0) return;

      const file = input.files[0];
      const isImage = file.type.startsWith('image/') ||
                      /\.(heic|heif|jpe?g|png|webp)$/i.test(file.name);

      // Non-image (e.g. PDF proof of purchase) — pass through unchanged
      if (!isImage) return;

      const hint = getOrCreateHint(input);
      hint.textContent = 'Processing photo…';
      hint.classList.add('warranty-form__hint--processing');
      input.disabled = true;

      try {
        const processed = await processImage(file);
        processedFiles[fieldName] = processed;
        const kb = Math.round(processed.size / 1024);
        hint.textContent = `Ready (${kb} KB)`;
        hint.classList.remove('warranty-form__hint--processing');
        hint.classList.add('warranty-form__hint--ok');
      } catch (err) {
        console.error('[image processing]', err);
        hint.textContent = 'Could not process photo. Please try a different one.';
        hint.classList.remove('warranty-form__hint--processing');
        hint.classList.add('warranty-form__hint--error');
        // Clear the input so the user must pick another file
        input.value = '';
      } finally {
        input.disabled = false;
      }
    });
  });

  function getOrCreateHint(input) {
    // Prefer a hint element already adjacent to the input's parent, or make one
    let hint = input.parentElement.querySelector('.warranty-form__hint--dynamic');
    if (!hint) {
      hint = document.createElement('small');
      hint.className = 'warranty-form__hint warranty-form__hint--dynamic';
      input.insertAdjacentElement('afterend', hint);
    }
    hint.classList.remove('warranty-form__hint--ok', 'warranty-form__hint--error', 'warranty-form__hint--processing');
    return hint;
  }

  // ---------- Image processing pipeline ----------
  async function processImage(file) {
    let workingFile = file;

    // Step 1: HEIC → JPEG conversion (iPhone photos)
    const isHeic = /image\/hei[cf]/i.test(file.type) ||
                   /\.(heic|heif)$/i.test(file.name);
    if (isHeic) {
      await loadHeic2Any();
      const convertedBlob = await window.heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.9,
      });
      const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
      const newName = file.name.replace(/\.hei[cf]$/i, '.jpg');
      workingFile = new File([blob], newName, { type: 'image/jpeg' });
    }

    // Step 2: Resize + JPEG re-encode
    return await compressImage(workingFile);
  }

  function compressImage(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = () => { img.src = reader.result; };
      reader.onerror = () => reject(new Error('Could not read file'));

      img.onload = () => {
        const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);

        canvas.toBlob(
          blob => {
            if (!blob) return reject(new Error('Canvas encoding failed'));
            const newName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
            resolve(new File([blob], newName, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          JPEG_QUALITY
        );
      };
      img.onerror = () => reject(new Error('Could not decode image'));

      reader.readAsDataURL(file);
    });
  }

  // Lazy-load heic2any only when needed
  let heic2anyLoading = null;
  function loadHeic2Any() {
    if (window.heic2any) return Promise.resolve();
    if (heic2anyLoading) return heic2anyLoading;

    heic2anyLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = HEIC2ANY_URL;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load HEIC converter'));
      document.head.appendChild(script);
    });
    return heic2anyLoading;
  }

  // ---------- Error helpers ----------
  function setError(name, message) {
    const errEl = form.querySelector(`[data-error-for="${name}"]`);
    if (errEl) errEl.textContent = message;
    const field = form.querySelector(`[name="${name}"]`);
    if (field && field.closest('.warranty-form__field')) {
      field.closest('.warranty-form__field').classList.add('is-invalid');
    }
  }
  function clearError(name) {
    const errEl = form.querySelector(`[data-error-for="${name}"]`);
    if (errEl) errEl.textContent = '';
    const field = form.querySelector(`[name="${name}"]`);
    if (field && field.closest('.warranty-form__field')) {
      field.closest('.warranty-form__field').classList.remove('is-invalid');
    }
  }
  function clearAllErrors() {
    form.querySelectorAll('.warranty-form__error').forEach(el => el.textContent = '');
    form.querySelectorAll('.is-invalid').forEach(el => el.classList.remove('is-invalid'));
    formError.hidden = true;
    formError.textContent = '';
  }

  // ---------- Validation ----------
  function validate() {
    clearAllErrors();
    let ok = true;

    const required = form.querySelectorAll('[required]');
    required.forEach(field => {
      const isFile = field.type === 'file';
      const hasProcessed = isFile && processedFiles[field.name];
      const hasRawFile = isFile && field.files && field.files.length > 0;
      const empty = isFile ? (!hasProcessed && !hasRawFile) : !field.value;
      if (empty) {
        setError(field.name, 'This field is required.');
        ok = false;
      }
    });

    const email = form.querySelector('[name="email"]');
    if (email.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
      setError('email', 'Please enter a valid email address.');
      ok = false;
    }

    if (phoneInput.value && phoneInput.value.replace(/\D/g, '').length < 10) {
      setError('phone', 'Please enter a valid 10-digit phone number.');
      ok = false;
    }

    return ok;
  }

  // ---------- Submit ----------
  form.addEventListener('submit', async function (e) {
    e.preventDefault();
    if (!validate()) {
      const firstError = form.querySelector('.is-invalid');
      if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setLoading(true);

    try {
      // Build FormData manually so we can substitute processed files
      const fd = new FormData();

      form.querySelectorAll('input, select, textarea').forEach(el => {
        if (!el.name) return;
        if (el.type === 'file') {
          const processed = processedFiles[el.name];
          if (processed) {
            fd.append(el.name, processed, processed.name);
          } else if (el.files && el.files.length > 0) {
            for (const f of el.files) fd.append(el.name, f, f.name);
          }
        } else if (el.type === 'checkbox' || el.type === 'radio') {
          if (el.checked) fd.append(el.name, el.value);
        } else {
          fd.append(el.name, el.value);
        }
      });

      fd.append('_submitted_at', new Date().toISOString());
      fd.append('_page_url', window.location.href);

      const res = await fetch(endpoint, {
        method: 'POST',
        body: fd
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(errText || `Submission failed (${res.status})`);
      }

      form.hidden = true;
      const notice = root.querySelector('.warranty-form__notice');
      if (notice) notice.hidden = true;
      successBox.hidden = false;
      successBox.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      console.error('[warranty-form]', err);
      formError.hidden = false;
      formError.textContent = 'Something went wrong submitting your form. Please try again, or email support@kanefootwear.com directly.';
    } finally {
      setLoading(false);
    }
  });

  function setLoading(loading) {
    submitBtn.disabled = loading;
    spinner.hidden = !loading;
    submitLabel.textContent = loading ? 'Submitting…' : 'Submit';
  }
})();